import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronRight, Clock, Euro } from "lucide-react";
import { scrollToId } from "@/lib/api";
import { useLang } from "@/lib/i18n";

const EASE = [0.22, 1, 0.36, 1];

export const ServiceHero = ({ page }) => {
  const { t } = useLang();
  const ts = t.service;
  return (
  <section data-testid="service-hero" className="relative pt-32 md:pt-44 pb-16 md:pb-24 border-b border-line overflow-hidden">
    <div className="absolute -top-24 -right-24 w-[36rem] h-[36rem] rounded-full bg-gold/[0.05] blur-3xl pointer-events-none" aria-hidden="true" />
    <div className="relative max-w-7xl mx-auto px-6 md:px-10">
      <motion.nav
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
        aria-label="Breadcrumb"
        data-testid="service-breadcrumb"
        className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-dim mb-10"
      >
        <Link to="/" className="hover:text-gold transition-colors duration-300">{ts.home}</Link>
        <ChevronRight size={12} />
        <Link to="/uslugi" className="hover:text-gold transition-colors duration-300">{ts.all}</Link>
        <ChevronRight size={12} />
        <span className="text-gold">{page.num}</span>
      </motion.nav>

      <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-end">
        <div className="lg:col-span-8">
          <motion.p
            initial={{ opacity: 0, x: -24 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.1, ease: EASE }}
            className="font-mono text-xs uppercase tracking-[0.4em] text-gold mb-8 flex items-center gap-4"
          >
            <span className="inline-block w-10 h-px bg-gold" aria-hidden="true" />
            {ts.label} · {page.num}
          </motion.p>
          <h1 data-testid="service-title" className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-mist mb-8 overflow-hidden">
            <motion.span initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 1, delay: 0.2, ease: EASE }} className="block">
              {page.title}
            </motion.span>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6, ease: EASE }}
            data-testid="service-intro"
            className="text-dim text-base md:text-lg max-w-2xl leading-relaxed mb-10"
          >
            {page.intro}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.75, ease: EASE }}
            className="flex flex-wrap gap-4"
          >
            <button
              data-testid="service-cta-contact"
              onClick={() => scrollToId("#contact")}
              className="group inline-flex items-center gap-3 bg-gold text-ink px-7 py-3.5 text-sm font-medium tracking-wide hover:bg-gold-light transition-colors duration-300"
            >
              {ts.cta}
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <Link
              to="/uslugi"
              data-testid="service-cta-all"
              className="inline-flex items-center gap-3 border border-line text-mist px-7 py-3.5 text-sm tracking-wide hover:border-gold hover:text-gold transition-colors duration-300"
            >
              {ts.allBtn}
            </Link>
          </motion.div>
        </div>

        <motion.aside
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.9, ease: EASE }}
          data-testid="service-facts"
          className="lg:col-span-4 border border-gold/40 bg-gold/[0.04] p-8 grid gap-6"
        >
          <div className="flex items-start gap-4">
            <Euro size={18} className="text-gold mt-1 shrink-0" strokeWidth={1.5} />
            <div>
              <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-dim">{ts.price}</p>
              <p data-testid="service-price" className="font-mono text-xl text-gold mt-1">{page.priceFrom}</p>
            </div>
          </div>
          <div className="flex items-start gap-4">
            <Clock size={18} className="text-gold mt-1 shrink-0" strokeWidth={1.5} />
            <div>
              <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-dim">{ts.timeline}</p>
              <p data-testid="service-timeline" className="text-mist mt-1">{page.timeline}</p>
            </div>
          </div>
        </motion.aside>
      </div>
    </div>
  </section>
  );
};
